<template>
    <v-dialog
      v-model="showForm"
      max-width="600px"
      persistent
      class="video-conference-form-dialog"
    >
      <v-card>
        <v-toolbar class="mb-4">
          <v-toolbar-title>{{$t('videoconf.menuTitle')}}</v-toolbar-title>
        </v-toolbar>
            <v-form
              v-model="valid"
              ref="form">
              <v-card height="600px" style="overflow-y: scroll" elevation="0">
              <v-overlay absolute :value="loading">
                <v-progress-circular size="80" indeterminate>
                </v-progress-circular>
              </v-overlay>
              <fieldset class="rounded-lg ma-6 pa-2">
                <legend>{{ $t('videoconf.form.general') }}</legend>
                <!-- name -->
                <v-text-field v-model="videoConference.name" :rules="[rules.required, rules.name]" prepend-icon="mdi-rename"
                  :label="$t('videoconf.form.fields.name')" class="my-2" data-testid="video-conference-form-name-field">
                </v-text-field>
                <!-- expireAt at -->
                <ExpireAtDatePicker :expires="videoConference.expireAt" @setModel="setExpireAt"/>
                <!-- admins -->
                <v-autocomplete
                  v-model="videoConference.adminsId" data-testid="video-conference-admin" prepend-icon="mdi-account-tie" :rules="[rules.required]"
                  :items="usersList" item-text="name" item-value="userId" clearable chips small-chips :placeholder="$t('videoconf.form.emptyAdminPlaceholder')"
                  multiple :label="$t('videoconf.form.fields.admins')" class="my-2">
                  <template v-slot:item="data">
                    <AutocompleteItem :data="data"/>
                  </template>
                  <template v-slot:selection="data">
                    <AutocompleteSelection :data="data" @remove="removeAdmin(data.item)"/>
                  </template>
                </v-autocomplete>
                <!-- usersList -->
                <v-autocomplete
                  v-model="videoConference.usersId" data-testid="video-conference-users" prepend-icon="mdi-account-group"
                  :items="usersList" item-text="name" item-value="userId" clearable chips small-chips :placeholder="$t('videoconf.form.emptyUserPlaceholder')"
                  multiple :label="$t('videoconf.form.fields.users')" class="mt-2">
                  <template v-slot:item="data">
                    <AutocompleteItem :data="data"/>
                  </template>
                  <template v-slot:selection="data">
                    <AutocompleteSelection :data="data" @remove="removeUser(data.item)"/>
                  </template>
                </v-autocomplete>
              </fieldset>
              <!-- share screen -->
              <fieldset v-if="screenSharingModuleEnabled" class="rounded-lg ma-6 pa-2" data-testid="video-conference-form-screenshare-fieldset">
                <legend>{{ $t('videoconf.form.shareScreen') }}</legend>
                  <v-checkbox v-model="videoConference.sharedScreenAdmin" :label="$t('videoconf.form.fields.enableForAdmin')"></v-checkbox>
                  <v-checkbox v-model="videoConference.sharedScreenGuest" :label="$t('videoconf.form.fields.enableForGuest')"></v-checkbox>
              </fieldset>
              <!-- recording -->
              <fieldset v-if="recordingModuleEnabled" class="rounded-lg ma-6 pa-2" data-testid="video-conference-form-recording-fieldset">
                <legend>{{ $t('videoconf.form.recording') }}</legend>
                  <v-checkbox v-model="videoConference.recordingAdmin" :label="$t('videoconf.form.fields.enableForAdmin')"></v-checkbox>
                  <v-checkbox v-model="videoConference.recordingGuest" :label="$t('videoconf.form.fields.enableForGuest')"></v-checkbox>
              </fieldset>
              <!-- telephony -->
              <fieldset v-if="telephonyModuleEnabled" class="rounded-lg ma-6 pa-2" data-testid="video-conference-form-telephony-fieldset">
                <legend>{{ $t('videoconf.form.telephony') }}</legend>
                  <AvailableExtensions :extension="parseInt(videoConference.username)" @setModel="setUsername"/>
                  <v-text-field v-model="videoConference.callerIdName" prepend-icon="mdi-card-account-phone"
                  :label="$t('videoconf.form.fields.callerIdName')" ></v-text-field>
                  <v-text-field v-model="videoConference.callerIdNumber" prepend-icon="mdi-card-account-phone"
                  :label="$t('videoconf.form.fields.callerIdNumber')" ></v-text-field>
              </fieldset>
              <!-- prompt -->
              <fieldset class="rounded-lg ma-6 pa-2" data-testid="video-conference-form-prompt-fieldset">
                <legend>{{ $t('videoconf.form.prompt') }}</legend>
                <v-text-field v-model="videoConference.roomNumber" prepend-icon="mdi-identifier"
                  :label="$t('videoconf.form.fields.roomNumber')" type="number"></v-text-field>
                <v-text-field v-model="videoConference.roomPin" prepend-icon="mdi-lock"
                  :label="$t('videoconf.form.fields.roomPin')" type="number"></v-text-field>
              </fieldset>
              </v-card>
            </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- card btn -->
          <v-btn @click="$emit('close')" text>
            {{ $t('videoconf.form.cancel') }}
          </v-btn>
          <v-btn @click="save" color="primary" text :disabled="!valid" :loading="loading" data-testid="video-conference-form-submit-btn">
            {{ isUpdate ? $t('videoconf.form.update') : $t('videoconf.form.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AutocompleteItem from '@/components/video_conferences/list/form/autocomplete_item'
import AutocompleteSelection from '@/components/video_conferences/list/form/autocomplete_selection'
import ExpireAtDatePicker from '@/components/video_conferences/list/form/expires_date_picker'
import AvailableExtensions from '@/components/available_extensions'
import VideoConference from 'Store/class/video_conference'
import API from '@/api'

export default {
  name: 'video-conference-form',
  components: { AutocompleteItem, AutocompleteSelection, AvailableExtensions, ExpireAtDatePicker },
  data () {
    return {
      valid: null,
      loading: false,
      confUsers: null,
      rules: {
        required: v => !!v || this.$t('videoconf.form.required'),
        name: v => v.match(/^[a-zA-Z0-9]+$/) || this.$t('videoconf.form.invalidName')
      }
    }
  },
  props: {
    showForm: { type: Boolean, default: false },
    isUpdate: { type: Boolean, default: false },
    videoConference: { type: VideoConference, default () { return new VideoConference() } }
  },
  computed: {
    ...mapGetters({
      usersList: 'users/listAll',
      recordingModuleEnabled: 'videoConferenceConfig/enableRecording',
      telephonyModuleEnabled: 'videoConferenceConfig/enableTelephony',
      screenSharingModuleEnabled: 'videoConferenceConfig/enableScreenSharing'
    })
  },
  watch: {
    showForm (val) {
      if (val && this.isUpdate && this.videoConference?.id) {
        this.loadVideoConferenceDetails()
      }
    }
  },
  methods: {
    ...mapActions({
      create: 'videoconfer/create',
      update: 'videoconfer/update'
    }),

    async loadVideoConferenceDetails () {
      this.loading = true
      const response = await API.getVideoConference(this.videoConference.id)
      const videoConference = response?.data || {}
      this.videoConference.name = videoConference.name
      this.videoConference.username = videoConference.username
      this.videoConference.expireAt = videoConference.expire_at
      this.videoConference.sharedScreenAdmin = videoConference.shared_screen_admin
      this.videoConference.sharedScreenGuest = videoConference.shared_screen_guest
      this.videoConference.recordingAdmin = videoConference.recording_admin
      this.videoConference.recordingGuest = videoConference.recording_guest
      this.videoConference.callerIdName = videoConference.caller_id_name
      this.videoConference.callerIdNumber = videoConference.caller_id_number
      this.videoConference.roomNumber = videoConference.room_number
      this.videoConference.roomPin = videoConference.room_pin
      this.videoConference.admins = videoConference.admins
      this.videoConference.users = videoConference.users
      this.videoConference.adminsId = videoConference.admins.map(admin => admin.id)
      this.videoConference.usersId = videoConference.users.map(user => user.id)
      this.loading = false
    },

    removeAdmin (item) {
      const index = this.videoConference.adminsId.indexOf(item.userId)
      if (index >= 0) this.videoConference.adminsId.splice(index, 1)
    },

    removeUser (item) {
      const index = this.videoConference.usersId.indexOf(item.userId)
      if (index >= 0) this.videoConference.usersId.splice(index, 1)
    },

    // setters for child components
    setUsername (username) { this.videoConference.username = username },
    setExpireAt (expireAt) { this.videoConference.expireAt = expireAt },

    // API expect params keys as snake case
    camelToSnake (object) {
      const newObject = {}

      for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
          let newKey = key
          if (/[A-Z]/.test(key)) {
            newKey = key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`)
          }
          newObject[newKey] = object[key]
        }
      }

      return newObject
    },

    save () {
      this.loading = true
      this.videoConference.adminsId = Array.isArray(this.videoConference.adminsId) ? this.videoConference.adminsId.join(',') : []
      this.videoConference.usersId = Array.isArray(this.videoConference.usersId) ? this.videoConference.usersId.join(',') : []
      const snakeCaseObject = this.camelToSnake(this.videoConference)
      if (this.isUpdate) {
        this.update(snakeCaseObject).then(() => {
          this.loading = false
          this.$emit('close')
        })
      } else {
        this.create(snakeCaseObject).then(() => {
          this.loading = false
          this.$emit('close')
        })
      }
    }
  }
}
</script>

<style>

</style>
